import { useT } from '../i18n'
import type { SeriesDecision } from './exportSeries'
import type { SeriesPlan } from './seriesPlan'
import type { SeriesFrame } from './seriesCatalog'

/**
 * Выбор человека, когда часть кадров серии не готова.
 *
 * Картинку без данных серия наружу не отдаёт (см. exportSeries), но пропажа
 * пропаже рознь: тайлы при живом поле — это минуты ожидания, а поля, которого
 * нет в базе, ждать бессмысленно. Поэтому окно не отказывает, а показывает
 * ПОИМЁННО, что с каждым кадром, и предлагает решить.
 *
 * Это единственное окно на пути скачивания и появляется оно только когда есть
 * о чём спрашивать. Кнопку «Загрузить» на карте оно не касается — та по
 * решению владельца не спрашивает никогда.
 */

const STATE_KEY = {
  ready: 'map.export.plan.stateReady',
  render: 'map.export.plan.stateRender',
  missing: 'map.export.plan.stateMissing',
} as const

export function SeriesPlanDialog({
  plan,
  onChoose,
}: {
  plan: SeriesPlan
  onChoose: (decision: SeriesDecision) => void
}) {
  const t = useT()
  const canWait = plan.renderable.length > 0
  const rows: { frame: SeriesFrame; state: keyof typeof STATE_KEY }[] = [
    ...plan.ready.map(frame => ({ frame, state: 'ready' as const })),
    ...plan.renderable.map(frame => ({ frame, state: 'render' as const })),
    ...plan.missing.map(frame => ({ frame, state: 'missing' as const })),
  ]

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-abyss/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-hull bg-deep p-5 shadow-console">
        <h2 className="text-lg font-semibold text-foam">{t('map.export.plan.title')}</h2>
        <p className="mt-1 text-sm text-mist">
          {canWait ? t('map.export.plan.introWait') : t('map.export.plan.introPartial')}
        </p>
        {/* Числа ПЕРЕД списком: кадров теперь до двух десятков, и «дорисовать
            14» против «дорисовать 2» — разные решения, а прокручивать список
            ради их подсчёта человек не станет. */}
        <p className="mt-1 text-xs text-faint">
          {t('map.export.plan.counts', {
            ready: plan.ready.length,
            render: plan.renderable.length,
            missing: plan.missing.length,
          })}
        </p>

        <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-hull">
          <table className="w-full text-sm">
            <tbody>
              {rows.map(({ frame, state }) => (
                <tr key={frame.id} className="border-t border-hull first:border-t-0">
                  <td className="px-3 py-2 text-foam">{t(frame.labelKey)}</td>
                  <td className="px-3 py-2 text-right">
                    <span className={state === 'ready' ? 'text-sonar' : 'text-buoy'}>
                      {t(STATE_KEY[state])}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={() => onChoose('cancel')}
            className="rounded-lg px-4 py-2 text-sm text-mist hover:bg-trench hover:text-foam"
          >
            {t('common.cancel')}
          </button>
          {/* Готовых кадров нет вовсе — предлагать «скачать готовые» значит
              предлагать пустой архив. Такой кнопки не бывает. */}
          {plan.ready.length > 0 && (
          <button
            type="button"
            onClick={() => onChoose('partial')}
            className="rounded-lg border border-hull px-4 py-2 text-sm text-foam hover:border-sonar hover:bg-haze"
          >
            {t('map.export.plan.partial', { count: plan.ready.length })}
          </button>
          )}
          {canWait && (
            <button
              type="button"
              onClick={() => onChoose('wait')}
              className="rounded-lg bg-gradient-to-b from-sonar to-sonar-dim px-4 py-2 text-sm font-semibold text-abyss shadow-sonar hover:from-sonar hover:to-sonar"
            >
              {t('map.export.plan.wait')}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
